import { Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNotices, useAckIsolationNotice } from "@/hooks/useNotices";

/**
 * One-time notice shown after the paper/highlight isolation migration.
 * Explains that canonical PDFs users previously uploaded are no longer
 * shared between accounts; users must re-upload or re-fetch for private
 * viewing. Dismissed via POST /api/users/me/notices/isolation/ack.
 */
export function IsolationNoticeBanner() {
  const { data } = useNotices();
  const ack = useAckIsolationNotice();

  const notice = data?.find((n) => n.type === "isolation-notice");
  if (!notice || notice.ack) return null;

  return (
    <div
      role="status"
      className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="flex-1 space-y-1">
        <p className="font-medium">
          PDFs and highlights are now private to your account.
        </p>
        <p className="text-xs leading-relaxed text-amber-800/90 dark:text-amber-100/80">
          PDFs you uploaded previously are no longer shared between users. Open-access
          PDFs continue to load automatically. For paywalled papers, re-upload your own
          copy from the paper detail panel — your uploads stay in your account and are
          never shown to other users.
        </p>
      </div>
      <Button
        size="xs"
        variant="outline"
        onClick={() => ack.mutate()}
        disabled={ack.isPending}
        className="shrink-0 border-amber-300 bg-amber-100/60 text-amber-900 hover:bg-amber-200/70 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-100"
      >
        Got it
        <X className="size-3" aria-hidden="true" />
      </Button>
    </div>
  );
}
